import React from 'react'
import { useNavigate } from 'react-router-dom'
import PageShell from '../../components/PageShell/PageShell'
import { PlansArt } from '../../components/PageShell/HeroArt'
import {
    IconShield, IconDevices, IconExport, IconHelp, IconBolt, IconApprove,
} from '../../components/HomeContent/Icons'
import './Plans.scss';

/* ---------------------------------------------------------------
   PRICING PLACEHOLDERS — the `price` and `note` values on Basic and
   Pro are stand-ins. Replace them with your real commercial numbers
   before this page goes live. The trial length is set in TRIAL_DAYS.
   --------------------------------------------------------------- */
const TRIAL_DAYS = 60;

const PLANS = [
    {
        id: 'free',
        name: 'Free',
        price: 'Free',
        note: `full access for ${TRIAL_DAYS} days`,
        blurb: 'Try the whole product on your own data before you decide anything.',
        cta: 'Start free trial',
        flag: 'No card needed',
        featured: false,
        features: [
            `Every module unlocked for ${TRIAL_DAYS} days`,
            'Unlimited users during the trial',
            'Import your real team and projects',
            'Keep everything you create when you upgrade',
            'No card required to start',
            'Email support from day one',
        ],
    },
    {
        id: 'basic',
        name: 'Basic',
        price: '$5',
        note: 'per user / month',
        blurb: 'The daily essentials for a team running its own week.',
        cta: 'Choose Basic',
        featured: false,
        features: [
            'Up to 25 users',
            'Work reports and timesheets',
            'Leave requests and approvals',
            'Projects and teams',
            'Dashboard and exports',
            'Email support',
        ],
    },
    {
        id: 'pro',
        name: 'Pro',
        price: '$10',
        note: 'per user / month',
        blurb: 'Everything you had in the trial, kept switched on.',
        cta: 'Choose Pro',
        flag: 'Most chosen',
        featured: true,
        features: [
            'Everything in Basic',
            'Unlimited users',
            'Team and project reports',
            'Recruitment, jobs and talent pool',
            'Skill search across the company',
            'Asset management and help desk',
            'Single sign-on with Microsoft',
            'Priority support',
        ],
    },
];

const INCLUDED = [
    { icon: <IconBolt />, title: 'Set up in an afternoon', copy: 'Import your team, set roles, configure the policy once. No consultants required.' },
    { icon: <IconShield />, title: 'Role-based access', copy: 'People see their own work, managers see their team. On every plan, not just the top one.' },
    { icon: <IconDevices />, title: 'Every device', copy: 'Phone, tablet, laptop — the same screens, nothing to install or keep in sync.' },
    { icon: <IconApprove />, title: 'Approvals built in', copy: 'Leave, reports and timesheets route to the right person from day one.' },
    { icon: <IconExport />, title: 'Exports, always', copy: 'Your data leaves in a click, in a format a spreadsheet reads. No export fees.' },
    { icon: <IconHelp />, title: 'A real help desk', copy: 'Raise a ticket and follow it to done — the same one your own team uses internally.' },
];

const FAQS = [
    { q: `What happens after the ${TRIAL_DAYS} days?`, a: 'Nothing disappears. Your account moves to read-only until you pick Basic or Pro, and everything you logged during the trial is still there when you do.' },
    { q: 'Is the trial really full access?', a: 'Yes — every module, no user cap and no feature held back. The point is to judge the product on your own data, not a cut-down version of it.' },
    { q: 'Can we change plan later?', a: 'Yes, in either direction, and the change applies from your next billing period. Nothing is archived or locked when you move down.' },
    { q: 'What counts as a user?', a: 'Anyone who signs in during the month. People who have left, and accounts you have deactivated, are not counted.' },
    { q: 'Is there a minimum commitment?', a: 'No. Basic and Pro are month to month and you can stop whenever you like.' },
    { q: 'What happens to our data if we leave?', a: 'Every report exports in a click while your account is active, and we keep the data available for a grace period after you close it so nothing is lost in the handover.' },
];

const Plans = () => {
    const navigate = useNavigate();

    return (
        <PageShell
            eyebrow="Plans"
            title="Priced per person, not per module"
            lede="Start with 60 days of everything, free. Decide which plan fits once you have run a couple of real reporting cycles."
            art={<PlansArt />}
        >
            {/* tiers */}
            <section className="pg-sec pg-sec--light">
                <div className="container">
                    <ul className="pl-grid">
                        {PLANS.map((p) => (
                            <li className={`pl-card${p.featured ? ' is-featured' : ''}`} key={p.id}>
                                {p.flag && (
                                    <span className={`pl-flag${p.featured ? '' : ' pl-flag--soft'}`}>{p.flag}</span>
                                )}

                                <h2 className="pl-name">{p.name}</h2>
                                <p className="pl-blurb">{p.blurb}</p>

                                <p className="pl-price">
                                    <strong>{p.price}</strong>
                                    <span>{p.note}</span>
                                </p>

                                <button
                                    type="button"
                                    className={`pg-btn ${p.featured ? 'pg-btn--primary' : 'pg-btn--ghost'} pl-cta`}
                                    onClick={() => navigate('/login')}
                                >
                                    {p.cta}
                                </button>

                                <ul className="pg-checks pl-features">
                                    {p.features.map((f) => <li key={f}>{f}</li>)}
                                </ul>
                            </li>
                        ))}
                    </ul>

                    <p className="pl-foot">
                        Prices exclude applicable taxes. Annual billing is available on Basic and Pro. Need something beyond Pro? Talk to us.
                    </p>
                </div>
            </section>

            {/* included everywhere */}
            <section className="pg-sec pg-sec--wash">
                <div className="container">
                    <header className="pg-head pg-head--center">
                        <p className="pg-eyebrow">On every plan</p>
                        <h2 className="pg-title">The basics are never an upsell</h2>
                    </header>
                    <ul className="pg-grid">
                        {INCLUDED.map((i) => (
                            <li className="pg-card" key={i.title}>
                                <span className="pg-icon">{i.icon}</span>
                                <h3 className="pg-h3">{i.title}</h3>
                                <p>{i.copy}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* faq */}
            <section className="pg-sec pg-sec--tint">
                <div className="container">
                    <header className="pg-head pg-head--center">
                        <p className="pg-eyebrow">Before you ask</p>
                        <h2 className="pg-title pg-title--grad">The questions we get most</h2>
                    </header>

                    <ul className="pl-faq">
                        {FAQS.map((f) => (
                            <li key={f.q}>
                                <h3 className="pg-h3">{f.q}</h3>
                                <p>{f.a}</p>
                            </li>
                        ))}
                    </ul>

                    <div className="pl-faq-cta">
                        <p className="pg-lede">Still unsure which plan fits? Ask us — it takes one email.</p>
                        <button type="button" className="pg-btn pg-btn--primary" onClick={() => navigate('/contact')}>
                            Contact Us
                        </button>
                    </div>
                </div>
            </section>
        </PageShell>
    )
}

export default Plans
